// Deep research layer for agricultural data
class ResearchService {
  constructor() {
    this.cache = new Map()
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutes
  }

  // Cache helper
  getCachedData(key) {
    const cached = this.cache.get(key)
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data
    }
    return null
  }

  setCachedData(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() })
  }

  // Get crop information for your country (Thailand-focused)
  async getCropInfo(cropType, region = null) {
    const cacheKey = `crop_${cropType}_${region || 'all'}`
    const cached = this.getCachedData(cacheKey)
    if (cached) return cached

    try {
      // Thailand crop data (simplified for demo)
      const cropData = {
        rice: {
          name: 'ข้าว (Rice)',
          seasons: ['ประมาณเดือนพฤษภาคม - ตุลาคม', 'ประมาณเดือนพฤศจิกายน - เมษายน'],
          growingPeriod: 120, // days
          waterRequirement: 'สูง (High)',
          optimalTemp: '25-32°C',
          regions: {
            central: 'ภาคกลาง - พื้นที่เพาะปลูกหลัก',
            north: 'ภาคเหนือ - ข้าวหอมมะลิ',
            northeast: 'ภาคตะวันออกเฉียงเหนือ - ข้าวไร่นา',
            south: 'ภาคใต้ - พื้นที่จำกัด'
          },
          marketPrice: {
            current: 15000, // THB per ton
            unit: 'บาท/ตัน',
            trend: 'stable',
            lastUpdated: new Date().toISOString()
          },
          diseases: ['ใบจุด', 'รากเน่า', 'แมลงศัตรูพืช'],
          bestPractices: [
            'ควบคุมน้ำในนาอย่างเหมาะสม',
            'ใช้ปุ๋ยอินทรีย์ร่วมกับปุ๋ยเคมี',
            'ตรวจสอบสัญญาณโรคเป็นประจำ'
          ]
        },
        cassava: {
          name: 'มันสำปะหลัง (Cassava)',
          seasons: ['ประมาณเดือนพฤษภาคม - ธันวาคม'],
          growingPeriod: 300,
          waterRequirement: 'ปานกลาง (Medium)',
          optimalTemp: '25-30°C',
          regions: {
            northeast: 'ภาคตะวันออกเฉียงเหนือ - พื้นที่เพาะปลูกหลัก',
            north: 'ภาคเหนือ - พื้นที่ที่สูงขึ้น'
          },
          marketPrice: {
            current: 2500,
            unit: 'บาท/ตัน',
            trend: 'increasing',
            lastUpdated: new Date().toISOString()
          }
        },
        rubber: {
          name: 'ยางพารา (Rubber)',
          seasons: ['ตลอดปี (All year)'],
          growingPeriod: 3650, // 10 years
          waterRequirement: 'สูง (High)',
          optimalTemp: '25-28°C',
          regions: {
            south: 'ภาคใต้ - พื้นที่เพาะปลูกหลัก',
            east: 'ภาคตะวันออก - พื้นที่ขยายตัว'
          },
          marketPrice: {
            current: 60,
            unit: 'บาท/กิโลกรัม',
            trend: 'volatile',
            lastUpdated: new Date().toISOString()
          }
        },
        sugarcane: {
          name: 'อ้อย (Sugarcane)',
          seasons: ['ประมาณเดือนพฤศจิกายน - มีนาคม'],
          growingPeriod: 365,
          waterRequirement: 'สูง (High)',
          optimalTemp: '20-30°C',
          regions: {
            central: 'ภาคกลาง',
            north: 'ภาคเหนือ',
            northeast: 'ภาคตะวันออกเฉียงเหนือ'
          },
          marketPrice: {
            current: 1200,
            unit: 'บาท/ตัน',
            trend: 'stable',
            lastUpdated: new Date().toISOString()
          }
        }
      }

      const data = cropData[cropType.toLowerCase()] || null
      this.setCachedData(cacheKey, data)
      return data
    } catch (error) {
      console.error('Failed to fetch crop info:', error)
      return null
    }
  }

  // Get weather alerts and forecasts
  async getWeatherAlerts(region) {
    const cacheKey = `weather_alerts_${region}`
    const cached = this.getCachedData(cacheKey)
    if (cached) return cached

    try {
      // Simulated weather alerts for Thailand
      const alerts = [
        {
          id: 1,
          type: 'warning',
          title: 'ฝนตกหนักในภาคตะวันออกเฉียงเหนือ',
          description: 'คาดว่าจะมีฝนตกหนักในพื้นที่จังหวัดขอนแก่น อุดรธานี และนครพนม',
          regions: ['ขอนแก่น', 'อุดรธานี', 'นครพนม'],
          severity: 'high',
          startTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          endTime: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
          recommendations: [
            'เลื่อนการพ่นยาหรือปุ๋ย',
            'ตรวจสอบระบบระบายน้ำในนา',
            'เตรียมอุปกรณ์ป้องกันฝน'
          ]
        },
        {
          id: 2,
          type: 'info',
          title: 'อุณหภูมิสูงในภาคกลาง',
          description: 'อุณหภูมิอาจสูงถึง 38°C ในพื้นที่ภาคกลาง',
          regions: ['กรุงเทพมหานคร', 'นนทบุรี', 'ปทุมธานี', 'สมุทรปราการ'],
          severity: 'medium',
          startTime: new Date().toISOString(),
          endTime: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
          recommendations: [
            'ให้น้ำพืชในช่วงเช้าหรือเย็น',
            'ใช้ผ้าคลุมพืชเพื่อลดอุณหภูมิ',
            'ตรวจสอบความชื้นในดิน'
          ]
        }
      ]

      this.setCachedData(cacheKey, alerts)
      return alerts
    } catch (error) {
      console.error('Failed to fetch weather alerts:', error)
      return []
    }
  }

  // Get market prices and trends
  async getMarketPrices(crops) {
    const cacheKey = `market_prices_${crops.join(',')}`
    const cached = this.getCachedData(cacheKey)
    if (cached) return cached

    try {
      // Simulated market data from Thai agricultural markets
      const prices = {
        rice: {
          current: 15000,
          previous: 14800,
          unit: 'บาท/ตัน',
          change: 1.35,
          trend: 'up',
          markets: [
            { name: 'ตลาดกลางกรุงเทพ', price: 15200, volume: 'สูง' },
            { name: 'ตลาดข้าวเชียงใหม่', price: 14900, volume: 'ปานกลาง' },
            { name: 'ตลาดข้าวอุดรธานี', price: 15100, volume: 'สูง' }
          ],
          forecast: 'คาดว่าราคาจะเพิ่มขึ้นในช่วงเดือนหน้าเนื่องจากความต้องการจากตลาดส่งออก'
        },
        cassava: {
          current: 2500,
          previous: 2400,
          unit: 'บาท/ตัน',
          change: 4.17,
          trend: 'up',
          markets: [
            { name: 'ตลาดขอนแก่น', price: 2550, volume: 'สูง' },
            { name: 'ตลาดอุดรธานี', price: 2480, volume: 'ปานกลาง' }
          ],
          forecast: 'ราคามีแนวโน้มขึ้นจากความต้องการในตลาดจีน'
        },
        rubber: {
          current: 60,
          previous: 58,
          unit: 'บาท/กิโลกรัม',
          change: 3.45,
          trend: 'up',
          markets: [
            { name: 'ตลาดยางสุราษฎร์ธานี', price: 61, volume: 'สูง' },
            { name: 'ตลาดยางสงขลา', price: 59, volume: 'ปานกลาง' }
          ],
          forecast: 'ราคายางยังคงผันผวนตามราคาน้ำมันโลก'
        }
      }

      const result = {}
      crops.forEach(crop => {
        result[crop] = prices[crop] || null
      })

      this.setCachedData(cacheKey, result)
      return result
    } catch (error) {
      console.error('Failed to fetch market prices:', error)
      return {}
    }
  }

  // Get government policies and subsidies
  async getGovernmentPolicies() {
    const cacheKey = 'government_policies'
    const cached = this.getCachedData(cacheKey)
    if (cached) return cached

    try {
      const policies = [
        {
          id: 1,
          title: 'โครงการช่วยเหลือราคาข้าว',
          description: 'รัฐบาลช่วยเหลือราคาข้าวที่ชาวนาขายแล้ว 15,000 บาท/ตัน',
          eligibility: ['เกษตรกรที่ปลูกข้าว', 'มีพื้นที่ไม่เกิน 30 ไร่'],
          benefits: ['รับเงินชดเชยความเสียหาย', 'ความคุ้มครองราคา'],
          application: 'สามารถสมัครผ่านศูนย์บริการการเกษตรของจังหวัด',
          deadline: '30 กันยายน 2567',
          status: 'active'
        },
        {
          id: 2,
          title: 'โครงการส่งเสริมการเกษตรอินทรีย์',
          description: 'สนับสนุนการเปลี่ยนผ่านสู่การเกษตรอินทรีย์',
          eligibility: ['เกษตรกรที่สนใจเปลี่ยนผ่าน', 'มีพื้นที่ปลูก'],
          benefits: ['รับสนับสนุนค่าใช้จ่าย', 'การฝึกอบรมฟรี', 'ตลาดรับจำนำนวน'],
          application: 'สมัครออนไลน์ผ่านเว็บไซต์กระทรวงเกษตรและสหกรณ์การ',
          deadline: '31 ธันวาคม 2567',
          status: 'active'
        },
        {
          id: 3,
          title: 'โครงการปรับปรุงพันธุ์พืช',
          description: 'สนับสนุนการใช้พันธุ์พืชที่ได้รับการรับรอง',
          eligibility: ['เกษตรกรทุกราย'],
          benefits: ['รับเมล็ดพันธุ์ฟรี', 'คำแนะนำจากผู้เชี่ยวชาญ'],
          application: 'ติดต่อสำนักงานเกษตรจังหวัด',
          deadline: 'ตลอดปี',
          status: 'active'
        }
      ]

      this.setCachedData(cacheKey, policies)
      return policies
    } catch (error) {
      console.error('Failed to fetch government policies:', error)
      return []
    }
  }

  // Get pest and disease alerts
  async getPestAlerts(region, cropType) {
    const cacheKey = `pest_alerts_${region}_${cropType}`
    const cached = this.getCachedData(cacheKey)
    if (cached) return cached

    try {
      const alerts = [
        {
          id: 1,
          pest: 'หัวหนอนเจาะลำต้นข้าว',
          crop: 'rice',
          severity: 'high',
          affectedArea: 'ภาคกลางตอนบน',
          symptoms: ['ลำต้นถูกเจาะ', 'ใบเหลือง', 'ต้นอ่อนแน่น'],
          treatment: [
            'ใช้สารเคมีกำจัดแมลง',
            'ทำลายต้นที่ติดเชื้อ',
            'ปลูกข้าวพันธุ์ที่ทนทาน'
          ],
          prevention: [
            'ไถพระยุทธ์ให้ลึก',
            'ทำลายซากพืช',
            'หมุนเวียนพืช'
          ]
        },
        {
          id: 2,
          pest: 'โรคใบจุดมันสำปะหลัง',
          crop: 'cassava',
          severity: 'medium',
          affectedArea: 'ภาคตะวันออกเฉียงเหนือ',
          symptoms: ['จุดสีน้ำตาลบนใบ', 'ใบเหลืองและร่วง'],
          treatment: [
            'ใช้เชื้อราที่เป็นมิตร',
            'ลดความชื้นในแปลง',
            'ตัดใบที่ติดเชื้อ'
          ],
          prevention: [
            'ปลูกในระยะที่เหมาะสม',
            'ควบคุมการให้น้ำ',
            'ใช้พันธุ์ที่ทนโรค'
          ]
        }
      ]

      this.setCachedData(cacheKey, alerts)
      return alerts
    } catch (error) {
      console.error('Failed to fetch pest alerts:', error)
      return []
    }
  }

  // Get best practices for specific crop and region
  async getBestPractices(cropType, region) {
    const cacheKey = `best_practices_${cropType}_${region}`
    const cached = this.getCachedData(cacheKey)
    if (cached) return cached

    try {
      const practices = {
        rice: {
          central: {
            soil: 'ดินเหนียวที่มีความอุ้มน้ำดี',
            water: 'ใช้ระบบน้ำท่วมและระบายน้ำสลับกัน',
            fertilizer: 'ปุ๋ยไนโตรเจน 16-20-0 อัตรา 25 กก./ไร่',
            pestControl: 'ใช้ศัตรูพืชและสารเคมีที่เหมาะสม',
            harvesting: 'เก็บเกี่ยวเมื่อข้าวสุก 90-95%'
          },
          north: {
            soil: 'ดินร่วนที่ระบายน้ำดี',
            water: 'ใช้น้ำจากแม่น้ำและระบบชลประทาน',
            fertilizer: 'ปุ๋ยอินทรีย์ร่วมกับปุ๋ยเคมี',
            pestControl: 'เน้นการป้องกันมากกว่าการรักษา',
            harvesting: 'เก็บเกี่ยวเมื่อข้าวหอมมะลิสุกเต็มที่'
          }
        }
      }

      const result = practices[cropType]?.[region] || null
      this.setCachedData(cacheKey, result)
      return result
    } catch (error) {
      console.error('Failed to fetch best practices:', error)
      return null
    }
  }
}

export const researchService = new ResearchService()
export default researchService
